import { useState, useEffect, useRef, useCallback } from 'react'
import type { CanonicalDocument } from '../cdm/types'

type PreviewCanonicalFn = (doc: CanonicalDocument, payload: string, signal: AbortSignal) => Promise<Blob>

interface PreviewState {
  pdfUrl: string | null
  loading: boolean
  error: string | null
}

/**
 * Live-preview lifecycle for the WYSIWYG editor's unsaved CanonicalDocument (Part 1.7 of the plan).
 * Not a reuse of usePreviewRender: that hook requires a non-null templateVersionId and a fixed
 * previewFn(payload, format, signal) shape built around an already-saved template version, neither
 * of which fits preview-canonical's `document + payload (+ optional templateVersionId)` call
 * shape for an in-progress edit. Modeled closely on the same abort/generation-counted/object-URL
 * lifecycle instead.
 */
export function useCanonicalPreview(
  previewFn: PreviewCanonicalFn
): PreviewState & { trigger: (doc: CanonicalDocument, payload: string) => void } {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pendingUrl = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const genRef = useRef(0)

  const revokePending = useCallback(() => {
    if (pendingUrl.current) {
      URL.revokeObjectURL(pendingUrl.current)
      pendingUrl.current = null
    }
  }, [])

  const trigger = useCallback(async (doc: CanonicalDocument, payload: string) => {
    abortRef.current?.abort()
    abortRef.current = new AbortController()
    const gen = ++genRef.current

    setLoading(true)
    setError(null)

    try {
      const blob = await previewFn(doc, payload, abortRef.current.signal)
      if (gen !== genRef.current) return
      revokePending()
      const url = URL.createObjectURL(blob)
      pendingUrl.current = url
      setPdfUrl(url)
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return
      if (gen !== genRef.current) return
      setError(err instanceof Error ? err.message : 'Preview failed')
    } finally {
      if (gen === genRef.current) setLoading(false)
    }
  }, [previewFn, revokePending])

  useEffect(() => {
    return () => {
      revokePending()
      abortRef.current?.abort()
    }
  }, [revokePending])

  return { pdfUrl, loading, error, trigger }
}
